import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ThemeToggle } from "@/components/ThemeToggle";
import { useAuth } from "@/context/AuthContext";
import { AUTH } from "@/constants/testIds";
import { toast } from "sonner";
import { Sparkles } from "lucide-react";

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) {
      toast.success("Welcome back!");
      navigate(location.state?.from?.pathname || "/app/dashboard", { replace: true });
    } else {
      setError(res.error || "Login failed");
    }
  };

  return (
    <div className="grid min-h-screen grid-cols-1 md:grid-cols-2">
      {/* Left panel */}
      <div className="relative hidden overflow-hidden border-r border-border bg-card md:flex md:flex-col md:justify-between md:p-12">
        <div className="grid-pattern grid-pattern-fade absolute inset-0 opacity-60" />
        <div className="relative">
          <Link to="/" className="flex items-center gap-2">
            <img src="/lanex-mark.png" alt="LaneX Academy" className="h-10 w-auto object-contain bg-white/95 rounded-lg p-1 shadow-md ring-1 ring-primary/30 drop-shadow-[0_0_14px_rgba(0,238,199,0.45)]" />
            <span className="font-display text-lg font-black">LaneX Academy</span>
          </Link>
        </div>
        <div className="relative max-w-md">
          <Sparkles className="mb-4 h-8 w-8 text-primary" />
          <h1 className="font-display text-4xl font-black tracking-tighter">
            Your mentor <br /> <span className="gradient-cyan-lime">is ready.</span>
          </h1>
          <p className="mt-4 text-muted-foreground">
            Pick up where you left off — your streak, notes, and lessons are exactly as you left them.
          </p>
          <div className="mt-8 rounded-xl border border-border bg-background/50 p-4 backdrop-blur">
            <div className="label-mono text-[0.6rem]">Demo account</div>
            <div className="mt-1 font-mono text-sm">demo@lanex.io · Demo@Lanex2026</div>
          </div>
        </div>
        <div className="relative label-mono">// v1.0 · LaneX Academy</div>
      </div>

      {/* Right form */}
      <div className="relative flex flex-col justify-center px-6 py-12 md:px-16">
        <div className="absolute right-6 top-6"><ThemeToggle /></div>
        <div className="mx-auto w-full max-w-md">
          <div className="label-mono mb-2 text-primary">// sign in</div>
          <h2 className="font-display text-3xl font-black tracking-tighter">Welcome back.</h2>
          <p className="mt-2 text-muted-foreground text-sm">Sign in to continue your learning.</p>

          <form onSubmit={onSubmit} className="mt-8 space-y-5">
            <div>
              <Label htmlFor="email" className="label-mono">Email</Label>
              <Input
                id="email"
                data-testid={AUTH.emailInput}
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="mt-2 h-11 rounded-lg"
              />
            </div>
            <div>
              <Label htmlFor="password" className="label-mono">Password</Label>
              <Input
                id="password"
                data-testid={AUTH.passwordInput}
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="mt-2 h-11 rounded-lg"
              />
            </div>
            {error && <div data-testid={AUTH.errorText} className="rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</div>}
            <Button
              data-testid={AUTH.submitBtn}
              disabled={loading}
              type="submit"
              className="w-full h-11 rounded-full btn-neon font-mono text-xs uppercase tracking-[0.2em]"
            >
              {loading ? "Signing in…" : "Sign in"}
            </Button>
          </form>

          <div className="mt-6 text-sm text-muted-foreground">
            New to LaneX?{" "}
            <Link data-testid={AUTH.switchLink} to="/register" className="text-primary hover:underline">Create an account</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
